// Trang hiển thị danh sách đơn chờ phê duyệt
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Table, Card, Spin, message, Tag, Button, Modal, Descriptions, Input, InputNumber, Row, Col, Tooltip } from 'antd';
import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import api, { errorMessage } from '../api';

// Hàm chuyển status sang chữ và màu
const statusMap = {
  0: { text: 'Chờ phê duyệt', color: 'gold' },
  1: { text: 'Duyệt', color: 'green' },
  2: { text: 'Từ chối', color: 'red' },
  4: { text: 'Lock đơn phê duyệt', color: 'gray' },
};

const POLL_INTERVAL_MS = 3000;

const formatNumber = (v) => (v === null || v === undefined || v === '' ? '-' : Number(v).toLocaleString());

function playBeep() {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
  gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.3);
}

function ApproveList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchAccount, setSearchAccount] = useState('');
  const [searchPromo, setSearchPromo] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [notificationModal, setNotificationModal] = useState({
    isOpen: false,
    title: '',
    content: '',
  });
  const [manualPointsModal, setManualPointsModal] = useState({
    open: false,
    record: null,
    amount: null,
    submitting: false,
  });

  // Loading states cho từng action
  const [actionLoading, setActionLoading] = useState({});

  // Realtime states
  const [newItemIds, setNewItemIds] = useState(() => new Set());
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connected'); // connected, slow, error
  const [requesting, setRequesting] = useState(false);

  const dataRef = useRef([]);
  const isFirstLoadRef = useRef(true);
  const fetchingRef = useRef(false);
  const refetchRequestedRef = useRef(false);
  const autoRunningRef = useRef(false);
  // Đơn đã thử duyệt tự động trong phiên này (server vẫn chặn trùng, đây chỉ để không gọi lại liên tục)
  const autoAttemptedIdsRef = useRef(new Set());

  const showNotification = (title, content) => setNotificationModal({ isOpen: true, title, content });

  const runAutoApprove = useCallback(async (orders) => {
    if (autoRunningRef.current) return;
    const candidates = orders.filter(
      (o) => o.auto_approve && o.status !== 4 && !autoAttemptedIdsRef.current.has(o.id)
    );
    if (!candidates.length) return;

    autoRunningRef.current = true;
    try {
      for (const order of candidates) {
        autoAttemptedIdsRef.current.add(order.id);
        try {
          const res = await api.post(`/orders/${order.id}/auto-approve`);
          if (res.data?.data?.approved) {
            message.success(`Đã duyệt tự động đơn ${order.promo_code} - ${order.account}`, 2);
          }
        } catch (err) {
          if (err.response?.data?.approved) {
            message.error(`Đơn ${order.account} đã duyệt tự động nhưng cộng tiền lỗi: ${errorMessage(err)}`, 6);
          } else {
            autoAttemptedIdsRef.current.delete(order.id);
            console.error('Auto-approve error:', err);
          }
        }
      }
    } finally {
      autoRunningRef.current = false;
    }
  }, []);

  // Hàm lấy danh sách đơn chờ phê duyệt
  const fetchData = useCallback(async (showNewItemsAlert = false) => {
    if (fetchingRef.current) {
      if (!showNewItemsAlert) refetchRequestedRef.current = true;
      return;
    }
    fetchingRef.current = true;

    const isFirstLoad = isFirstLoadRef.current;
    if (isFirstLoad) setLoading(true);

    const startTime = Date.now();
    setRequesting(true);

    try {
      const response = await api.get('/orders/pending');
      const requestDuration = Date.now() - startTime;

      if (requestDuration > 5000) {
        setConnectionStatus('slow');
        if (showNewItemsAlert) {
          message.warning(`⚠️ Kết nối chậm: ${Math.round(requestDuration / 1000)}s`);
        }
      } else if (requestDuration > 2000) {
        setConnectionStatus('slow');
      } else {
        setConnectionStatus('connected');
      }

      const newData = response.data?.data || [];
      const prevData = dataRef.current;

      // Kiểm tra có đơn mới không
      if (showNewItemsAlert && !isFirstLoad && prevData.length > 0) {
        const currentIds = new Set(prevData.map((item) => item.id));
        const newItems = newData.filter((item) => !currentIds.has(item.id));

        if (newItems.length > 0) {
          setNewItemIds(new Set(newItems.map((item) => item.id)));

          try {
            playBeep();
          } catch (e) {
            console.log('Không thể phát âm thanh:', e.message);
          }

          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('🔔 Đơn khuyến mãi mới!', {
              body: `Có ${newItems.length} đơn mới cần xử lý`,
              icon: '/logogk88.png',
              tag: 'promo-notification',
              requireInteraction: false
            });
          }

          message.success({
            content: `🔔 Có ${newItems.length} đơn khuyến mãi mới!`,
            duration: 4,
            style: { marginTop: '20vh' }
          });

          setTimeout(() => setNewItemIds(new Set()), 10000);
        }
      }

      dataRef.current = newData;
      setData(newData);
      setLastUpdateTime(new Date());
      isFirstLoadRef.current = false;

      runAutoApprove(newData);
    } catch (error) {
      const requestDuration = Date.now() - startTime;
      setConnectionStatus('error');

      if (!showNewItemsAlert) {
        message.error(errorMessage(error, 'Lỗi khi tải dữ liệu!'));
      } else {
        message.error(`❌ Mất kết nối sau ${Math.round(requestDuration / 1000)}s`);
      }
    } finally {
      if (isFirstLoad) setLoading(false);
      setRequesting(false);
      fetchingRef.current = false;
      if (refetchRequestedRef.current) {
        refetchRequestedRef.current = false;
        fetchData();
      }
    }
  }, [runAutoApprove]);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    fetchData();
    const interval = setInterval(() => fetchData(true), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const runAction = async (actionKey, fn) => {
    if (actionLoading[actionKey]) return;
    setActionLoading((prev) => ({ ...prev, [actionKey]: true }));
    try {
      await fn();
    } finally {
      setActionLoading((prev) => ({ ...prev, [actionKey]: false }));
      fetchData();
    }
  };

  const showApproveError = (err) => {
    if (err.response?.data?.approved) {
      showNotification('Duyệt OK, Cộng tiền thất bại', errorMessage(err));
    } else {
      showNotification('Thao tác thất bại', errorMessage(err, 'Lỗi hệ thống khi duyệt đơn.'));
    }
  };

  const handleApprove = (record, status) => {
    // Mã cần nhập điểm thủ công: mở popup nhập số điểm trước khi duyệt
    if (status === 1 && record.manual_points) {
      const preset = Number(record.bonus_amount);
      setManualPointsModal({
        open: true,
        record,
        amount: !isNaN(preset) && preset > 0 ? preset : null,
        submitting: false,
      });
      return;
    }

    return runAction(`approve_${record.id}_${status}`, async () => {
      try {
        if (status === 1) {
          const res = await api.post(`/orders/${record.id}/approve`);
          const result = res.data?.data || {};
          showNotification(
            'Thành công',
            `Duyệt và cộng ${formatNumber(result.bonus_amount)} KM '${record.promo_code}' cho '${record.account}' thành công! (Vòng cược: ${formatNumber(result.audit_amount)})`
          );
        } else {
          await api.post(`/orders/${record.id}/reject`);
          showNotification('Thành công', `Đã từ chối đơn KM '${record.promo_code}' của tài khoản '${record.account}'.`);
        }
      } catch (err) {
        showApproveError(err);
      }
    });
  };

  const closeManualPointsModal = () => {
    setManualPointsModal({ open: false, record: null, amount: null, submitting: false });
  };

  const handleManualPointsApproveConfirm = async () => {
    const { record, amount } = manualPointsModal;
    if (!record) return;

    const bonusAmount = Number(amount);
    if (isNaN(bonusAmount) || bonusAmount <= 0) {
      message.warning('Vui lòng nhập số điểm cộng lớn hơn 0.');
      return;
    }

    setManualPointsModal((prev) => ({ ...prev, submitting: true }));
    await runAction(`approve_${record.id}_1`, async () => {
      try {
        const res = await api.post(`/orders/${record.id}/approve`, { bonus_amount: bonusAmount });
        closeManualPointsModal();
        showNotification(
          'Thành công',
          `Duyệt ${(record.promo_code || '').toUpperCase()} và cộng ${bonusAmount.toLocaleString()} điểm cho '${record.account}' thành công! (Vòng cược: ${formatNumber(res.data?.data?.audit_amount)})`
        );
      } catch (err) {
        if (err.response?.data?.approved) closeManualPointsModal();
        showApproveError(err);
      }
    });
    setManualPointsModal((prev) => ({ ...prev, submitting: false }));
  };

  const handleLock = (record) => runAction(`lock_${record.id}`, async () => {
    const hideLoading = message.loading('Đang xử lý yêu cầu khóa đơn...', 0);
    try {
      await api.post(`/orders/${record.id}/lock`);
      showNotification('Thành công', `Đã khóa đơn của tài khoản '${record.account}'.`);
    } catch (err) {
      showNotification('Thất bại', errorMessage(err, 'Lỗi hệ thống khi khóa đơn.'));
    } finally {
      hideLoading();
    }
  });

  const handleUnlock = (record) => runAction(`unlock_${record.id}`, async () => {
    const hideLoading = message.loading('Đang xử lý yêu cầu mở khóa...', 0);
    try {
      await api.post(`/orders/${record.id}/unlock`);
      showNotification('Thành công', `Đã mở khóa đơn của tài khoản '${record.account}'.`);
    } catch (err) {
      showNotification('Thất bại', errorMessage(err, 'Lỗi hệ thống khi mở khóa.'));
    } finally {
      hideLoading();
    }
  });

  const handleBatchReject = async () => {
    setBatchLoading(true);
    try {
      const res = await api.post('/orders/batch-reject', { ids: selectedRowKeys });
      const { success = 0, failed = [] } = res.data?.data || {};
      showNotification(
        'Hoàn tất từ chối hàng loạt',
        `Đã từ chối thành công ${success} đơn. Thất bại hoặc bị chặn: ${failed.length} đơn.`
      );
      setSelectedRowKeys([]);
    } catch (err) {
      showNotification('Thao tác thất bại', errorMessage(err, 'Lỗi hệ thống khi từ chối hàng loạt.'));
    } finally {
      setBatchLoading(false);
      fetchData();
    }
  };

  const isNewItem = (record) => newItemIds.has(record.id);

  // Định nghĩa các cột cho bảng
  const columns = [
    { title: "STT", dataIndex: "stt", key: "stt", width: 60 },
    { title: "ID", dataIndex: "id", key: "id", width: 70 },
    { title: "Tài khoản", dataIndex: "account", key: "account" },
    {
      title: "Tên đầy đủ",
      dataIndex: "full_name",
      key: "full_name",
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.duplicateInfo?.fullname > 1 && (
            <Tag color="blue" size="small">
              {record.duplicateInfo.fullname}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Mã khuyến mãi",
      dataIndex: "promo_code",
      key: "promo_code",
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.auto_approve && <Tag color="purple">Tự động</Tag>}
        </div>
      ),
    },
    { title: "Tiền nạp", dataIndex: "deposit_amount", key: "deposit_amount" },
    { title: "Tiền thưởng", dataIndex: "bonus_amount", key: "bonus_amount" },
    {
      title: "Vòng cược",
      dataIndex: "audit_amount",
      key: "audit_amount",
      render: (value, record) => (
        <Tooltip title={record.audit_formula}>
          {record.manual_points ? <Tag color="orange">Nhập tay</Tag> : <span>{formatNumber(value)}</span>}
          {!record.has_rule && <div><Tag color="red">Chưa cấu hình</Tag></div>}
        </Tooltip>
      ),
    },
    { title: "Đại lý", dataIndex: "franchisee", key: "franchisee" },
    { title: "VIP", dataIndex: "vip_level", key: "vip_level", width: 70 },
    {
      title: "Ngân hàng",
      dataIndex: "bank_name",
      key: "bank_name",
    },
    {
      title: "Số TK",
      dataIndex: "bank_account_number",
      key: "bank_account_number",
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.duplicateInfo?.bank_number > 1 && (
            <Tag color="blue" size="small">{record.duplicateInfo.bank_number}</Tag>
          )}
        </div>
      )
    },
    {
      title: "IP thiết bị",
      dataIndex: "device_ip",
      key: "device_ip",
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.duplicateInfo?.device_ip > 1 && (
            <Tag color="blue" size="small">
              {record.duplicateInfo.device_ip}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "ID thiết bị",
      dataIndex: "device_id",
      key: "device_id",
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.duplicateInfo?.device_id > 1 && (
            <Tag color="blue" size="small">
              {record.duplicateInfo.device_id}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status, record) => {
        const s = statusMap[status] || {
          text: "Không xác định",
          color: "default",
        };

        return (
          <div style={{ position: 'relative' }}>
            {isNewItem(record) && (
              <Tag
                color="red"
                size="small"
                style={{
                  marginBottom: 4,
                  animation: 'pulse 1.5s infinite',
                  fontWeight: 'bold'
                }}
              >
                🆕 MỚI
              </Tag>
            )}
            <Tag color={s.color}>{s.text}</Tag>
            {status === 4 && record.locked_by && (
              <div style={{ fontSize: 12, color: '#999' }}>bởi {record.locked_by}</div>
            )}
          </div>
        );
      },
    },
    { title: "Ngày tạo", dataIndex: "createdAt", key: "createdAt" },
    {
      title: "Chức năng",
      key: "action",
      fixed: "right",
      width: 300,
      render: (_, record) => {
        const isLocked = record.status === 4;
        const canAct = record.can_act;
        const iconStyle = { fontSize: 22 };

        const approveKey = `approve_${record.id}_1`;
        const rejectKey = `approve_${record.id}_2`;
        const lockKey = `lock_${record.id}`;
        const unlockKey = `unlock_${record.id}`;

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                type="link"
                icon={<EyeOutlined style={iconStyle} />}
                onClick={() => {
                  setSelectedRow(record);
                  setModalOpen(true);
                }}
              />
              {isLocked && canAct && (
                <Button
                  type="dashed"
                  size="small"
                  icon={<UnlockOutlined style={iconStyle} />}
                  loading={actionLoading[unlockKey]}
                  disabled={actionLoading[unlockKey]}
                  onClick={() => handleUnlock(record)}
                />
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {isLocked ? (
                canAct && (
                  <>
                    <Button
                      danger
                      size="small"
                      icon={<CloseCircleOutlined style={iconStyle} />}
                      loading={actionLoading[rejectKey]}
                      disabled={actionLoading[rejectKey]}
                      onClick={() => handleApprove(record, 2)}
                    />
                    <Button
                      type="primary"
                      size="small"
                      icon={<CheckCircleOutlined style={iconStyle} />}
                      loading={actionLoading[approveKey]}
                      disabled={actionLoading[approveKey]}
                      onClick={() => handleApprove(record, 1)}
                    />
                  </>
                )
              ) : (
                <>
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined style={iconStyle} />}
                    loading={actionLoading[approveKey]}
                    disabled={actionLoading[approveKey]}
                    onClick={() => handleApprove(record, 1)}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<CloseCircleOutlined style={iconStyle} />}
                    loading={actionLoading[rejectKey]}
                    disabled={actionLoading[rejectKey]}
                    onClick={() => handleApprove(record, 2)}
                  />
                  <Button
                    size="small"
                    icon={<LockOutlined style={iconStyle} />}
                    loading={actionLoading[lockKey]}
                    disabled={actionLoading[lockKey]}
                    onClick={() => handleLock(record)}
                  />
                </>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  // Lọc dữ liệu theo tài khoản và mã khuyến mãi, sau đó sắp xếp theo ngày tạo từ cũ đến mới
  const filteredData = data
    .filter(item =>
      (item.account || '').toLowerCase().includes(searchAccount.toLowerCase()) &&
      (item.promo_code || '').toLowerCase().includes(searchPromo.toLowerCase())
    )
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const detailFields = [
    { key: 'id', label: 'ID' },
    { key: 'account', label: 'Tài khoản' },
    { key: 'full_name', label: 'Tên đầy đủ' },
    { key: 'promo_code', label: 'Mã khuyến mãi' },
    { key: 'deposit_amount', label: 'Tiền nạp' },
    { key: 'bonus_amount', label: 'Tiền thưởng' },
    { key: 'audit_amount', label: 'Vòng cược', render: (v, row) => (row.manual_points ? 'Nhập tay khi duyệt' : formatNumber(v)) },
    { key: 'audit_formula', label: 'Công thức' },
    { key: 'franchisee', label: 'Đại lý' },
    { key: 'vip_level', label: 'VIP' },
    { key: 'bank_name', label: 'Ngân hàng' },
    { key: 'bank_account_number', label: 'Số TK' },
    { key: 'device_ip', label: 'IP thiết bị' },
    { key: 'device_id', label: 'ID thiết bị' },
    { key: 'device_type', label: 'Loại thiết bị' },
    { key: 'device_os', label: 'Hệ điều hành' },
    { key: 'status', label: 'Trạng thái', render: (v) => statusMap[v]?.text || v },
    { key: 'locked_by', label: 'Người khóa' },
    { key: 'memo', label: 'Ghi chú' },
    { key: 'user_approve', label: 'Người duyệt' },
    { key: 'time_approve', label: 'Thời gian duyệt' },
    { key: 'createdAt', label: 'Ngày tạo' },
    { key: 'updatedAt', label: 'Ngày cập nhật' },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.05); }
            100% { opacity: 1; transform: scale(1); }
          }

          .new-item-row {
            background: linear-gradient(90deg, #fff2f0 0%, #ffffff 100%) !important;
            border-left: 4px solid #ff4d4f !important;
          }

          .realtime-status {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 1000;
            background: rgba(0,0,0,0.8);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
          }
        `}
      </style>

      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Đơn chờ phê duyệt</span>
            <div style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 8 }}>
              {lastUpdateTime && (
                <span>
                  Cập nhật lần cuối: {lastUpdateTime.toLocaleTimeString()}
                  {newItemIds.size > 0 && (
                    <Tag color="red" size="small" style={{ marginLeft: 8 }}>
                      {newItemIds.size} mới
                    </Tag>
                  )}
                  {connectionStatus === 'slow' && (
                    <Tag color="orange" size="small" style={{ marginLeft: 8 }}>
                      Chậm
                    </Tag>
                  )}
                  {connectionStatus === 'error' && (
                    <Tag color="red" size="small" style={{ marginLeft: 8 }}>
                      Lỗi
                    </Tag>
                  )}
                </span>
              )}
              <Button
                size="small"
                onClick={() => {
                  try {
                    playBeep();
                    message.success('Test âm thanh thành công!');
                  } catch (e) {
                    message.error('Không thể phát âm thanh: ' + e.message);
                  }
                }}
                style={{ fontSize: 10 }}
              >
                🔊 Test
              </Button>
            </div>
          </div>
        }
      >
        <Row gutter={[8, 16]} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Input
            placeholder="Tìm kiếm tài khoản..."
            value={searchAccount}
            onChange={e => setSearchAccount(e.target.value)}
            allowClear
          />
        </Col>
        <Col span={8}>
          <Input
            placeholder="Tìm kiếm mã khuyến mãi..."
            value={searchPromo}
            onChange={e => setSearchPromo(e.target.value)}
            allowClear
          />
        </Col>
        <Col span={8}>
          <Button
            type="primary"
            danger
            onClick={handleBatchReject}
            disabled={!selectedRowKeys.length}
            loading={batchLoading}
          >
            Từ chối {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''} đơn
          </Button>
        </Col>
      </Row>
      <Spin spinning={loading}>
        <Table
          rowKey="id"
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          scroll={{ x: 1300 }}
          rowClassName={(record) => (isNewItem(record) ? 'new-item-row' : '')}
        />
      </Spin>
      <Modal
        open={modalOpen}
        title="Chi tiết đơn"
        onCancel={() => setModalOpen(false)}
        footer={null}
      >
        {selectedRow && (
          <Descriptions column={1} bordered size="small">
            {detailFields.map(field => (
              <Descriptions.Item label={field.label} key={field.key}>
                {field.render
                  ? field.render(selectedRow[field.key], selectedRow)
                  : selectedRow[field.key] ?? ''}
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Modal>
      <Modal
        open={manualPointsModal.open}
        title={`${(manualPointsModal.record?.promo_code || '').toUpperCase()} — Nhập số điểm cộng`}
        onOk={handleManualPointsApproveConfirm}
        onCancel={closeManualPointsModal}
        okText="Xác nhận duyệt"
        cancelText="Hủy"
        confirmLoading={manualPointsModal.submitting}
        destroyOnClose
      >
        {manualPointsModal.record && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ margin: 0, color: '#666' }}>
              Tài khoản: <strong>{manualPointsModal.record.account}</strong>
              {manualPointsModal.record.full_name && (
                <span> — {manualPointsModal.record.full_name}</span>
              )}
            </p>
            <div>
              <div style={{ marginBottom: 8 }}>Số điểm cộng:</div>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                precision={0}
                placeholder="Nhập số điểm cần cộng"
                value={manualPointsModal.amount}
                onChange={(val) => setManualPointsModal((prev) => ({ ...prev, amount: val }))}
                onPressEnter={handleManualPointsApproveConfirm}
                autoFocus
              />
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#999' }}>
              Vòng cược: {manualPointsModal.record.audit_formula}
            </p>
          </div>
        )}
      </Modal>
      <Modal
        open={notificationModal.isOpen}
        title={notificationModal.title}
        onOk={() => setNotificationModal({ isOpen: false, title: '', content: '' })}
        onCancel={() => setNotificationModal({ isOpen: false, title: '', content: '' })}
        footer={[
          <Button key="ok" type="primary" onClick={() => setNotificationModal({ isOpen: false, title: '', content: '' })}>
            Đã hiểu
          </Button>,
        ]}
      >
        <p>{notificationModal.content}</p>
      </Modal>

      <div className="realtime-status">
        {connectionStatus === 'connected' && (
          <>
            <span style={{ color: '#52c41a' }}>●</span> Realtime Active (3s)
          </>
        )}
        {connectionStatus === 'slow' && (
          <>
            <span style={{ color: '#faad14' }}>●</span> Kết nối chậm
          </>
        )}
        {connectionStatus === 'error' && (
          <>
            <span style={{ color: '#ff4d4f' }}>●</span> Mất kết nối
          </>
        )}
        {requesting && (
          <span style={{ color: '#1890ff', marginLeft: 8 }}>
            ⏳ Đang tải...
          </span>
        )}
      </div>
    </Card>
    </>
  );
}

export default ApproveList;
